import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Box, IconButton, Typography } from '@mui/material';
import { ReactComponent as VisibilityIcon } from '../../static/images/svg/ic_round-visibility.svg';
import { ReactComponent as EditIcon } from '../../static/images/svg/tabler_edit.svg';
import i18n from '../../i18n';
import apiManage, { apiManage_rbp } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import ParkModeTermsDetailDialog, {
    isParkTermsDetailVersioned,
    ParkTermsDetail,
    ParkTermsDetailLegacy,
    ParkTermsDetailVersioned,
    type ParkTermsDialogMode,
} from './park-mode-terms-detail.component';
import { RBP_MENU_SETTINGS } from '../../constants/rbp-menu-settings.constant';
import { getRBPStringId } from '../main/main-page-states';
import {
    isDeviceSystemVersionAtLeast,
    PARK_MODE_AGREEMENT_VERSION_MIN,
} from '../../utils/device-system-version';
import type { AgreementVersion } from '../../request/api/rbp/reserve';
import './scss/park-mode-terms.scss';

/** 列表行：旧版 settings 或新版协议分组 */
type TermsListRow = {
    /** 行稳定 key */
    key: string;
    /** 展示标题 */
    title: string;
    /** 新版当前版本号；旧版为空 */
    currentVersion: string;
    /** 打开弹窗用的详情 */
    detail: ParkTermsDetail;
};

/**
 * 将协议类型归一为 terms / privacy / disclaimer
 * @param agreementType - 后端 agreement_type
 */
const getAgreementTypeKind = (agreementType: string) => {
    /** 小写便于匹配 */
    const normalized = (agreementType || '').toLowerCase();
    if (normalized.includes('privacy')) {
        return 'privacy';
    }
    if (normalized.includes('disclaimer')) {
        return 'disclaimer';
    }
    return 'terms';
};

/**
 * 协议类型排序权重（条款 → 隐私 → 免责）
 * @param agreementType - 后端 agreement_type
 */
const getAgreementTypeOrder = (agreementType: string) => {
    /** 归一分类 */
    const kind = getAgreementTypeKind(agreementType);
    if (kind === 'terms') {
        return 0;
    }
    if (kind === 'privacy') {
        return 1;
    }
    return 2;
};

/**
 * 解析版本 content（对象或 JSON 字符串）
 * @param raw - 接口返回的 content
 */
const parseAgreementContent = (raw: AgreementVersion['content'] | string | undefined | null): {
    title: string;
    content: string;
} => {
    if (!raw) {
        return { title: '', content: '' };
    }
    if (typeof raw === 'string') {
        try {
            /** 解析后的对象 */
            const parsed = JSON.parse(raw) as { title?: string; content?: string };
            return {
                title: parsed?.title || '',
                content: parsed?.content || '',
            };
        } catch {
            return { title: '', content: raw };
        }
    }
    return {
        title: raw.title || '',
        content: raw.content || '',
    };
};

/**
 * 按协议类型取展示标题（TERMS_OF_USE 可用服务端 title）
 * @param agreementType - 协议类型
 * @param content - 解析后的正文对象
 */
const getAgreementTitle = (
    agreementType: string,
    content: { title: string; content: string },
): string => {
    /** 各类默认 i18n 标题 */
    const AGREEMENT_TITLE_FALLBACK: Record<string, string> = {
        terms: i18n.t('terms_setting_page_text_terms_and_rules'),
        privacy: i18n.t('terms_setting_page_text_privacy_policy'),
        disclaimer: i18n.t('terms_setting_page_text_disclaimer'),
    };
    /** 归一分类 */
    const kind = getAgreementTypeKind(agreementType);
    if (agreementType === 'TERMS_OF_USE') {
        return content?.title || AGREEMENT_TITLE_FALLBACK[kind];
    }
    return AGREEMENT_TITLE_FALLBACK[kind];
};

/**
 * 将单个版本映射为弹窗详情
 * @param version - 协议版本
 */
const mapVersionToDetail = (version: AgreementVersion): ParkTermsDetailVersioned => {
    /** 解析后的 content */
    const content = parseAgreementContent(version.content);
    return {
        id: version.id,
        title: getAgreementTitle(version.agreement_type, content),
        content,
        agreement_name: version.agreement_name,
        agreement_type: version.agreement_type,
        language: version.language,
        has_signed_record: version.has_signed_record,
        can_modify: version.can_modify,
    };
};

/**
 * 旧版默认三行
 */
const buildLegacyRows = (): TermsListRow[] => [
    {
        key: String(RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS),
        title: i18n.t('terms_setting_page_text_terms_and_rules'),
        currentVersion: '',
        detail: {
            id: RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS,
            title: i18n.t('terms_setting_page_text_terms_and_rules'),
            content: '',
        },
    },
    {
        key: String(RBP_MENU_SETTINGS.PARK_MODE_PRIVACY_POLICY),
        title: i18n.t('terms_setting_page_text_privacy_policy'),
        currentVersion: '',
        detail: {
            id: RBP_MENU_SETTINGS.PARK_MODE_PRIVACY_POLICY,
            title: i18n.t('terms_setting_page_text_privacy_policy'),
            content: '',
        },
    },
    {
        key: String(RBP_MENU_SETTINGS.PARK_MODE_DISCLAIMER),
        title: i18n.t('terms_setting_page_text_disclaimer'),
        currentVersion: '',
        detail: {
            id: RBP_MENU_SETTINGS.PARK_MODE_DISCLAIMER,
            title: i18n.t('terms_setting_page_text_disclaimer'),
            content: '',
        },
    },
];

/**
 * 条款设置：Shark >= 1.9.85 走协议版本接口；否则 settings 150/151/152。
 */
const ParkModeTermsTabPage: React.FC = () => {
    /** 当前聚焦餐厅 string_id（换店时重拉） */
    const restaurantStringId = getRBPStringId();
    /** 是否使用新版协议版本 API */
    const [useAgreementVersions, setUseAgreementVersions] = useState(false);
    /** 列表行 */
    const [rows, setRows] = useState<TermsListRow[]>(buildLegacyRows);
    /** 选中行下标 */
    const [selectedIndex, setSelectedIndex] = useState(-1);
    /** 弹窗是否打开 */
    const [dialogOpen, setDialogOpen] = useState(false);
    /** 弹窗模式 */
    const [dialogMode, setDialogMode] = useState<ParkTermsDialogMode>('edit');

    /**
     * 加载旧版 settings 条款
     */
    const loadLegacyTerms = useCallback(async () => {
        const result = await Promise.all([
            apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS),
            apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.PARK_MODE_PRIVACY_POLICY),
            apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.PARK_MODE_DISCLAIMER),
        ]);
        /** 各设置项 value */
        const content = result.map((res) => (!_.get(res, 'error') ? _.get(res, '[0].value', {}) : {}));
        setRows((prev) =>
            prev.map((item, index) => {
                /** 旧版详情 */
                const legacy = item.detail as ParkTermsDetailLegacy;
                return {
                    ...item,
                    title:
                        legacy.id === RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS
                            ? content[index]?.title || item.title
                            : item.title,
                    currentVersion: '',
                    detail: {
                        ...legacy,
                        title:
                            legacy.id === RBP_MENU_SETTINGS.PARK_MODE_TERMS_CONDITIONS
                                ? content[index]?.title || legacy.title
                                : legacy.title,
                        content: content[index]?.content || '',
                    },
                };
            }),
        );
    }, []);

    /**
     * 加载新版协议版本列表并按类型分组
     */
    const loadAgreementVersions = useCallback(async () => {
        const result = await apiManage_rbp.get_park_mode_agreement_versions();
        if (!_.get(result, 'error') && result?.ok) {
            /** 版本数组 */
            const versions = Array.isArray(result.versions) ? result.versions : [];
            /** 按 agreement_type 分组 */
            const groupedVersions = _.groupBy(versions, 'agreement_type');
            /** 分组后的列表行（过滤无当前版本的类型） */
            const nextRows: TermsListRow[] = [];
            Object.keys(groupedVersions).forEach((agreementType) => {
                /** 该类型下全部版本 */
                const versionsByType = groupedVersions[agreementType] || [];
                /** 当前生效版本 */
                const currentVersion =
                    versionsByType.find((item) => item.is_current) || versionsByType[0];
                if (!currentVersion) {
                    return;
                }
                /** 弹窗详情 */
                const detail = mapVersionToDetail(currentVersion);
                nextRows.push({
                    key: agreementType,
                    title: detail.title,
                    currentVersion: currentVersion.version || '',
                    detail,
                });
            });
            nextRows.sort(
                (a, b) =>
                    getAgreementTypeOrder((a.detail as ParkTermsDetailVersioned).agreement_type) -
                    getAgreementTypeOrder((b.detail as ParkTermsDetailVersioned).agreement_type),
            );
            setRows(nextRows);
        } else {
            setRows([]);
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
    }, []);

    /**
     * 初始化：先 version-check，再按阈值分流
     */
    const init = useCallback(async () => {
        setLoading(true);
        try {
            /** 餐厅 string_id */
            const sid = getRBPStringId();
            /** 版本检查结果 */
            const versionRes = await apiManage.get_restaurant_version_check(sid);
            /** 是否走新版 API */
            const useNew =
                !_.get(versionRes, 'error') &&
                isDeviceSystemVersionAtLeast(
                    String(versionRes?.latest_device_system_version ?? ''),
                    PARK_MODE_AGREEMENT_VERSION_MIN,
                );
            setUseAgreementVersions(useNew);
            if (useNew) {
                await loadAgreementVersions();
            } else {
                setRows(buildLegacyRows());
                await loadLegacyTerms();
            }
        } finally {
            setLoading(false);
        }
    }, [loadAgreementVersions, loadLegacyTerms]);

    useEffect(() => {
        void init();
    }, [init, restaurantStringId]);

    /**
     * 打开弹窗
     * @param index - 行下标
     * @param mode - 编辑或预览
     */
    const openDialog = (index: number, mode: ParkTermsDialogMode) => {
        setSelectedIndex(index);
        setDialogMode(mode);
        setDialogOpen(true);
    };

    /**
     * 保存条款（新旧分流）
     * @param detail - 弹窗回传详情
     */
    const handleSave = async (detail: ParkTermsDetail) => {
        setDialogOpen(false);
        setLoading(true);
        try {
            if (useAgreementVersions && isParkTermsDetailVersioned(detail)) {
                /** 新建发布参数 */
                const addParams = {
                    agreement_type: detail.agreement_type,
                    content: JSON.stringify(detail.content),
                };
                /** 原地修改参数 */
                const editParams = {
                    agreement_name: detail.agreement_name,
                    content: JSON.stringify(detail.content),
                };
                /** 保存结果 */
                const settingResult = detail.has_signed_record
                    ? await apiManage_rbp.post_park_mode_agreement_version(addParams)
                    : await apiManage_rbp.patch_park_mode_agreement_version(detail.id, editParams);
                if (!_.get(settingResult, 'error') && settingResult?.ok) {
                    GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
                    await loadAgreementVersions();
                } else {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                }
                return;
            }

            /** 旧版 settings 详情 */
            const legacy = detail as ParkTermsDetailLegacy;
            /** 当前行 */
            const item = rows[selectedIndex];
            const settingResult = await apiManage_rbp.patchSettingById(legacy.id, {
                title: legacy.title,
                content: legacy.content,
            });
            if (!_.get(settingResult, 'error')) {
                GlobalNotification.success(i18n.t('google_text_public_text_save_successfully'));
                if (item) {
                    const newData = [...rows];
                    newData[selectedIndex] = {
                        ...item,
                        title: legacy.title,
                        detail: legacy,
                    };
                    setRows(newData);
                }
                await loadLegacyTerms();
            } else {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setDialogOpen(false);
        setSelectedIndex(-1);
    };

    return (
        <Box className="park-mode-terms">
            <Box className="park-mode-terms__card">
                <Typography component="h1" className="park-mode-terms__title">
                    {i18n.t('park_mode_page_text_terms_setting')}
                </Typography>
                <Typography component="p" className="park-mode-terms__intro">
                    {i18n.t('park_mode_page_text_terms_setting_intro')}
                </Typography>
                <Box className="park-mode-terms__divider" aria-hidden />
                {rows.map((row, index) => (
                    <React.Fragment key={row.key}>
                        <Box className="park-mode-terms__row">
                            <Box className="park-mode-terms__row-text">
                                <Typography className="park-mode-terms__row-label">{row.title}</Typography>
                                {useAgreementVersions && row.currentVersion ? (
                                    <Typography className="park-mode-terms__row-version">
                                        {i18n.t('terms_setting_page_text_current_version')}:{' '}
                                        {row.currentVersion}
                                    </Typography>
                                ) : null}
                            </Box>
                            <Box className="park-mode-terms__row-actions">
                                <IconButton
                                    type="button"
                                    size="small"
                                    className="park-mode-terms__icon-btn"
                                    aria-label={i18n.t('menu_library_detail_edit_subcategory')}
                                    onClick={() => openDialog(index, 'edit')}
                                >
                                    <EditIcon className="park-mode-terms__icon-svg" aria-hidden />
                                </IconButton>
                                <IconButton
                                    type="button"
                                    size="small"
                                    className="park-mode-terms__icon-btn"
                                    aria-label={i18n.t('google_text_detail')}
                                    onClick={() => openDialog(index, 'preview')}
                                >
                                    <VisibilityIcon
                                        className="park-mode-terms__icon-svg park-mode-terms__visibility-svg"
                                        aria-hidden
                                    />
                                </IconButton>
                            </Box>
                        </Box>
                        {index < rows.length - 1 ? (
                            <Box className="park-mode-terms__divider" aria-hidden />
                        ) : null}
                    </React.Fragment>
                ))}
            </Box>
            {dialogOpen && selectedIndex >= 0 && rows[selectedIndex] && (
                <ParkModeTermsDetailDialog
                    open={dialogOpen}
                    mode={dialogMode}
                    detail={rows[selectedIndex].detail}
                    onClose={handleClose}
                    onSave={(d) => void handleSave(d)}
                />
            )}
        </Box>
    );
};

export default ParkModeTermsTabPage;
